import { useCallback, useEffect, useState } from 'react';
import type { UsageSummaryDto } from '@filmsloth/shared';
import { ApiError, api } from '../api/client.js';
import { Listbox } from '../components/Listbox.js';
import { TmdbSyncPanel } from '../components/TmdbSyncPanel.js';
import { useI18n, useT } from '../i18n/index.js';

interface StatsScreenProps {
  onBack: () => void;
}

/** Windows worth asking for. A week to see this week, a year to see whether. */
const RANGES = [7, 30, 90, 365] as const;

/**
 * The columns of the per-day table, in reading order.
 *
 * Chosen rather than "every counter there is": the point of this table is the
 * funnel — somebody arrived, a round got created, it actually started, it
 * found a match, the group settled on a film — and a table of thirty columns
 * would hide exactly that. Every counter is listed in full further down.
 */
const DAY_COLUMNS = [
  'visit',
  'new_devices',
  'session_created',
  'session_started',
  'session_matched',
  'film_chosen',
  'vote_cast',
] as const;

/**
 * How much the site is being used.
 *
 * Deliberately plain: this is the operator's instrument panel, not a page
 * anybody is being sold anything on. Counter names are shown as they are
 * stored (`session_created`, `view:home`) rather than translated — there are
 * thirty of them, they are the names the API answers with, and a label
 * somebody has to keep in step in two languages would buy nothing for the one
 * person who reads this screen.
 */
export function StatsScreen({ onBack }: StatsScreenProps) {
  const t = useT();
  const { locale } = useI18n();
  const [days, setDays] = useState<number>(30);
  const [summary, setSummary] = useState<UsageSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'forbidden' | 'failed' | null>(null);

  const load = useCallback((range: number) => {
    setLoading(true);
    setError(null);
    api
      .usageSummary(range)
      .then((next) => {
        setSummary(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        // A 401 and a 403 mean the same thing here — "not your dashboard" —
        // and deserve the plain sentence rather than a generic failure.
        const status = cause instanceof ApiError ? cause.status : 0;
        setError(status === 401 || status === 403 ? 'forbidden' : 'failed');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(days), [days, load]);

  const number = (value: number): string => value.toLocaleString(locale);

  const totalOf = (event: string): number =>
    summary?.totals.find((entry) => entry.event === event)?.total ?? 0;
  const todayOf = (event: string): number =>
    summary?.totals.find((entry) => entry.event === event)?.today ?? 0;
  const derivedTotal = (key: string): number =>
    summary?.series.reduce((sum, day) => sum + (day.counts[key] ?? 0), 0) ?? 0;

  const visits = summary?.totals.find((entry) => entry.event === 'visit');
  const appVisits = visits?.bySurface.app ?? 0;
  const webVisits = visits?.bySurface.web ?? 0;
  // Only over the two surfaces that actually said which they were: counting
  // `unknown` into the denominator would quietly report every crawler as a
  // vote against the app.
  const named = appVisits + webVisits;
  const appShare = named > 0 ? Math.round((appVisits / named) * 100) : null;

  // The tallest bar in the visits column, so the bars are relative to the
  // window being looked at rather than to some fixed idea of "busy".
  const peakVisits = Math.max(1, ...(summary?.series.map((day) => day.counts.visit ?? 0) ?? [0]));

  return (
    <div className="screen">
      <div className="row row--between">
        <h1>{t('stats.title')}</h1>
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          {t('common.back')}
        </button>
      </div>

      <p className="muted small">{t('stats.intro')}</p>

      <div className="panel stack stack--tight">
        <span className="field__label">{t('stats.range')}</span>
        <Listbox
          value={String(days)}
          onChange={(raw) => setDays(Number(raw))}
          ariaLabel={t('stats.range')}
          options={RANGES.map((range) => ({
            value: String(range),
            label: t('stats.rangeDays', { days: range }),
          }))}
        />
      </div>

      {loading ? <p className="muted">{t('common.loading')}</p> : null}

      {error === 'forbidden' ? (
        <div className="panel stack stack--tight">
          <p>{t('stats.forbidden')}</p>
        </div>
      ) : null}

      {error === 'failed' ? (
        <div className="panel stack stack--tight">
          <p className="muted">{t('stats.failed')}</p>
          <button type="button" className="btn btn--ghost" onClick={() => load(days)}>
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      {/* The catalog sync sits above the numbers: "did last night's run
          work?" is the question this screen is opened for most often. Same
          moderator gate as the usage figures, so it is only shown once those
          loaded. */}
      {summary && !error ? <TmdbSyncPanel /> : null}

      {summary && !error ? (
        <>
          {!summary.recording ? <p className="banner">{t('stats.recordingOff')}</p> : null}

          <div className="stats__tiles">
            <div className="stats__tile">
              <span className="stats__tile-value">{number(totalOf('visit'))}</span>
              <span className="muted small">{t('stats.visits')}</span>
              <span className="muted small">
                {t('stats.todayValue', { count: number(todayOf('visit')) })}
              </span>
            </div>

            <div className="stats__tile">
              <span className="stats__tile-value">
                {appShare === null ? '—' : t('stats.percent', { value: appShare })}
              </span>
              <span className="muted small">{t('stats.appShare')}</span>
              <span className="muted small">
                {t('stats.appVersusWeb', { app: number(appVisits), web: number(webVisits) })}
              </span>
            </div>

            <div className="stats__tile">
              <span className="stats__tile-value">{number(totalOf('session_created'))}</span>
              <span className="muted small">{t('stats.sessionsCreated')}</span>
              <span className="muted small">
                {t('stats.startedValue', { count: number(totalOf('session_started')) })}
              </span>
            </div>

            <div className="stats__tile">
              <span className="stats__tile-value">{number(totalOf('film_chosen'))}</span>
              <span className="muted small">{t('stats.filmsChosen')}</span>
              <span className="muted small">
                {t('stats.matchesValue', { count: number(totalOf('session_matched')) })}
              </span>
            </div>

            <div className="stats__tile">
              <span className="stats__tile-value">{number(derivedTotal('new_devices'))}</span>
              <span className="muted small">{t('stats.newDevices')}</span>
              <span className="muted small">{t('stats.newDevicesHint')}</span>
            </div>

            <div className="stats__tile">
              <span className="stats__tile-value">{number(derivedTotal('new_accounts'))}</span>
              <span className="muted small">{t('stats.newAccounts')}</span>
              <span className="muted small">
                {t('stats.accountsTotal', { count: number(summary.standing.accounts) })}
              </span>
            </div>
          </div>

          <div className="panel stack stack--tight">
            <h2>{t('stats.perDayTitle')}</h2>
            <p className="muted small">{t('stats.perDayHint')}</p>
            <div className="stats__scroll">
              <table className="stats__table">
                <thead>
                  <tr>
                    <th scope="col">{t('stats.day')}</th>
                    {DAY_COLUMNS.map((column) => (
                      <th key={column} scope="col">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Newest first: the answer to "did anything happen today"
                      should not be at the bottom of a year of rows. */}
                  {[...summary.series].reverse().map((day) => (
                    <tr key={day.day}>
                      <th scope="row">
                        <span className="stats__day">{day.day}</span>
                        <span
                          className="stats__bar"
                          style={{ width: `${((day.counts.visit ?? 0) / peakVisits) * 100}%` }}
                        />
                      </th>
                      {DAY_COLUMNS.map((column) => (
                        <td key={column}>
                          {day.counts[column] ? number(day.counts[column]) : '·'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel stack stack--tight">
            <h2>{t('stats.countersTitle')}</h2>
            <p className="muted small">{t('stats.countersHint')}</p>
            <div className="stats__scroll">
              <table className="stats__table">
                <thead>
                  <tr>
                    <th scope="col">{t('stats.counter')}</th>
                    <th scope="col">{t('stats.total')}</th>
                    <th scope="col">{t('stats.today')}</th>
                    <th scope="col">app</th>
                    <th scope="col">web</th>
                    <th scope="col">?</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.totals.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="muted">
                        {t('stats.empty')}
                      </td>
                    </tr>
                  ) : null}
                  {summary.totals.map((entry) => (
                    <tr key={entry.event}>
                      <th scope="row">{entry.event}</th>
                      <td>{number(entry.total)}</td>
                      <td>{entry.today ? number(entry.today) : '·'}</td>
                      <td>{entry.bySurface.app ? number(entry.bySurface.app) : '·'}</td>
                      <td>{entry.bySurface.web ? number(entry.bySurface.web) : '·'}</td>
                      <td>{entry.bySurface.unknown ? number(entry.bySurface.unknown) : '·'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel stack stack--tight">
            <h2>{t('stats.standingTitle')}</h2>
            <p className="muted small">{t('stats.standingHint')}</p>
            <dl className="stats__standing">
              <div>
                <dt className="muted small">{t('stats.standingDevices')}</dt>
                <dd>{number(summary.standing.devices)}</dd>
              </div>
              <div>
                <dt className="muted small">{t('stats.standingAccounts')}</dt>
                <dd>
                  {t('stats.confirmedOf', {
                    confirmed: number(summary.standing.accountsConfirmed),
                    total: number(summary.standing.accounts),
                  })}
                </dd>
              </div>
              <div>
                <dt className="muted small">{t('stats.standingFilms')}</dt>
                <dd>
                  {t('stats.communityOf', {
                    community: number(summary.standing.filmsCommunity),
                    total: number(summary.standing.films),
                  })}
                </dd>
              </div>
              <div>
                <dt className="muted small">{t('stats.standingClaims')}</dt>
                <dd>{number(summary.standing.factClaims)}</dd>
              </div>
              <div>
                <dt className="muted small">{t('stats.standingWatchlist')}</dt>
                <dd>{number(summary.standing.watchlistEntries)}</dd>
              </div>
              <div>
                <dt className="muted small">{t('stats.standingRatings')}</dt>
                <dd>{number(summary.standing.ratings)}</dd>
              </div>
              <div>
                <dt className="muted small">{t('stats.standingLiveSessions')}</dt>
                <dd>{number(summary.standing.liveSessions)}</dd>
              </div>
            </dl>
          </div>

          <p className="muted small">
            {t('stats.footer', {
              from: summary.from,
              to: summary.to,
              time: new Date(summary.generatedAt).toLocaleString(locale),
            })}
          </p>
        </>
      ) : null}
    </div>
  );
}
